import { useCallback, useEffect, useRef, useState } from 'react';
import { recordingAPI } from '../services/api';

const PAGE_LIMIT = 10;

const isCanceled = (err) =>
  err?.code === 'ERR_CANCELED' || err?.name === 'AbortError' || err?.name === 'CanceledError';

/**
 * Manages paginated recording data with AbortController cancellation.
 * Does NOT handle optimistic UI — that's React 19 useOptimistic in the component.
 * Exposes removeRecording / patchRecording to commit server-confirmed mutations.
 */
export function useRecordings() {
  const [recordings, setRecordings] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, total: 0, hasMore: true });
  const [status, setStatus] = useState('idle'); // idle | loading | success | loadingMore | error
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchPage = useCallback(async (page, replace = false) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setStatus(page === 1 ? 'loading' : 'loadingMore');

    try {
      const res = await recordingAPI.list(page, PAGE_LIMIT, abortRef.current.signal);
      const { recordings: rows, pagination: meta } = res.data;
      setRecordings((prev) => (replace ? rows : [...prev, ...rows]));
      setPagination(meta);
      setStatus('success');
      setError(null);
    } catch (err) {
      if (isCanceled(err)) return;
      setStatus('error');
      setError(err.message ?? 'Failed to load recordings');
    }
  }, []);

  useEffect(() => {
    fetchPage(1, true);
    return () => abortRef.current?.abort();
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (status !== 'success' || !pagination.hasMore) return;
    fetchPage(pagination.page + 1, false);
  }, [status, pagination.hasMore, pagination.page, fetchPage]);

  const refresh = useCallback(() => fetchPage(1, true), [fetchPage]);

  // Called after a confirmed server delete
  const removeRecording = useCallback((id) => {
    setRecordings((prev) => prev.filter((r) => r.id !== id));
    setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
  }, []);

  // Called after a confirmed server patch (e.g. transcript saved)
  const patchRecording = useCallback((id, patch) => {
    setRecordings((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  return {
    recordings,
    pagination,
    isLoading: status === 'loading',
    isFetchingMore: status === 'loadingMore',
    isError: status === 'error',
    error,
    hasMore: pagination.hasMore,
    loadMore,
    refresh,
    removeRecording,
    patchRecording,
  };
}
